import { useState, useEffect, ReactNode } from 'react';
import Select, { components, StylesConfig, SingleValue } from 'react-select';
import '@/styles/colors.css';

interface OptionType {
  label: string;
  value: string;
}

interface SelectDropdownProps {
  id?: string;
  name?: string;
  onChange?: (event: {
    target: { name: string; value: string; label?: string };
  }) => void;
  onBlur?: (event: { target: { name: string; value: string } }) => void;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  isActive?: boolean; // Highlight when filter is changed from default
  children?: ReactNode;
  style?: React.CSSProperties;
  borderless?: boolean;
  renderInPlace?: boolean;
}

const CustomDropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        width="12"
        height="12"
      >
        <path
          fill="none"
          stroke="var(--brand-dark-3)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M2 5l6 6 6-6"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

const SelectDropdown = ({
  id,
  name,
  onChange,
  onBlur,
  value,
  placeholder,
  disabled = false,
  invalid = false,
  isActive = false,
  children,
  style = {},
  borderless = false,
  renderInPlace = false,
}: SelectDropdownProps) => {
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Convert children <option> elements to options array
  // Flatten the children array in case .map() results are nested
  const childArray = children
    ? (Array.isArray(children) ? children : [children]).flat()
    : [];

  const options: OptionType[] = childArray
    .filter((child: any) => child?.type === 'option')
    .map((child: any) => ({
      label: child.props.children,
      value: child.props.value,
    }));

  // Set default value when options are populated
  useEffect(() => {
    if (options.length > 0) {
      const defaultOption =
        options.find((option) => String(option.value) === String(value)) ||
        null;

      if (defaultOption && selectedOption?.value !== defaultOption.value) {
        setSelectedOption(defaultOption);
      }
    }
  }, [options, value, selectedOption]);

  const handleChange = (newValue: SingleValue<OptionType>) => {
    setSelectedOption(newValue);
    if (onChange) {
      onChange({
        target: {
          name: name || id || '',
          value: newValue ? newValue.value : '',
          label: newValue ? newValue.label : '',
        },
      });
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      if (timeoutId) clearTimeout(timeoutId);

      const newTimeout = setTimeout(() => {
        onBlur({
          target: {
            name: name || id || '',
            value: selectedOption ? selectedOption.value : '',
          },
        });
      }, 100);

      setTimeoutId(newTimeout);
    }
  };

  const customStyles: StylesConfig<OptionType, false> = {
    control: (provided: any, state: any) => ({
      ...provided,
      padding: '2px 4px',
      border: borderless
        ? 'none'
        : `1px solid ${
            invalid
              ? 'var(--danger-dark-1)'
              : state.isDisabled
              ? 'var(--neutral-light-1)'
              : state.isFocused || isActive
              ? 'var(--brand-medium-2)'
              : 'var(--neutral-medium-1)'
          }`,
      borderRadius: 'var(--radius-md)',
      boxShadow: borderless
        ? 'none'
        : invalid
        ? state.isFocused
          ? '0 0 0 2px rgba(186, 27, 35, 0.15)'
          : 'none'
        : state.isFocused || isActive
        ? '0 0 0 2px rgba(78, 143, 255, 0.1)'
        : 'none',
      '&:hover': {
        borderColor: invalid
          ? 'var(--danger-dark-1)'
          : state.isDisabled
          ? 'var(--neutral-light-1)'
          : state.isFocused || isActive
          ? 'var(--brand-medium-2)'
          : 'var(--neutral-medium-1)',
      },
      backgroundColor: state.isDisabled
        ? 'var(--neutral-light-1)'
        : 'var(--white)',
      color: 'var(--neutral-dark-1)',
      minHeight: '42px',
      transition: 'all 0.2s',
      ...style,
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled
        ? 'var(--neutral-medium-2)'
        : 'var(--neutral-dark-1)',
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--neutral-dark-1)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'var(--brand-light-2)' : 'transparent',
      color: state.isSelected ? 'var(--brand-dark-1)' : 'var(--neutral-dark-1)',
      fontWeight: state.isSelected ? 600 : 400,
      '&:hover': {
        backgroundColor: 'var(--brand-light-2)',
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--neutral-medium-2)',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      paddingRight: '8px',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    menuList: (provided: any) => ({
      ...provided,
      overflowY: 'scroll',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'var(--neutral-light-1)',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'var(--neutral-medium-3)',
        borderRadius: '3px',
      },
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <Select<OptionType>
      id={id}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      isSearchable={true}
      isDisabled={disabled}
      components={{ DropdownIndicator: CustomDropdownIndicator }}
      className={invalid ? 'react-select invalid' : 'react-select'}
      classNamePrefix="react-select"
      menuPortalTarget={renderInPlace ? undefined : document.body}
      menuPosition={renderInPlace ? 'absolute' : 'fixed'}
      styles={customStyles}
    />
  );
};

export { SelectDropdown };
